import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  CreditCard,
  Undo2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

/**
 * Super-admin card — recent paid Stripe transactions across every pub, with
 * a one-click refund button for the rare "wrong match-day" mistake.
 *
 * Refund does a full-amount refund via Stripe and inline-flips our local
 * transaction to `payment_status=refunded` so the match-day gate re-locks
 * immediately (no wait for Stripe's `charge.refunded` webhook).
 */

function formatMoney(pence, currency) {
  if (typeof pence !== "number") return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(pence / 100);
  } catch {
    return `${(pence / 100).toFixed(2)} ${(currency || "gbp").toUpperCase()}`;
  }
}

function formatMatchDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ paymentStatus }) {
  if (paymentStatus === "refunded" || paymentStatus === "partially_refunded") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-100 border border-zinc-300 text-zinc-700 text-[10px] font-bold uppercase tracking-wider"
        data-testid="stripe-tx-status-refunded"
      >
        <Undo2 className="h-3 w-3" /> Refunded
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider"
      data-testid="stripe-tx-status-paid"
    >
      <CheckCircle2 className="h-3 w-3" /> Paid
    </span>
  );
}

export default function StripeTransactionsCard({
  pubSlug = "",
  onPubSlugChange,
  pubOptions = [],
} = {}) {
  const [state, setState] = useState({ kind: "loading" });
  const [refunding, setRefunding] = useState(null); // session_id being refunded

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (pubSlug) params.set("pub_slug", pubSlug);
      const { data } = await api.get(
        `/admin/payments/transactions?${params.toString()}`,
      );
      setState({ kind: "ready", rows: data || [] });
    } catch (err) {
      setState({ kind: "error", message: formatApiError(err) });
    }
  }, [pubSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefund = useCallback(
    async (row) => {
      const amount = formatMoney(row.amount_pence, row.currency);
      const matchDate = formatMatchDate(row.applied_match_date || row.match_date);
      const who = row.user_email || row.user_name || "this player";
      const reason = window.prompt(
        `Refund ${amount} to ${who} for match-day ${matchDate}?\n\n` +
          `Reason (optional, shown on Stripe dashboard):`,
        "Wrong match-day",
      );
      if (reason === null) return; // user cancelled the prompt
      setRefunding(row.session_id);
      try {
        await api.post("/admin/payments/refund", {
          session_id: row.session_id,
          reason: reason || undefined,
        });
        toast.success(`Refunded ${amount} to ${who}.`);
        await load();
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setRefunding(null);
      }
    },
    [load],
  );

  const rows = state.kind === "ready" ? state.rows : [];
  const paidRows = rows.filter((r) => r.payment_status === "paid");
  const refundedRows = rows.filter(
    (r) =>
      r.payment_status === "refunded" ||
      r.payment_status === "partially_refunded",
  );

  return (
    <section
      className="p-5"
      data-testid="stripe-transactions-card"
      aria-label="Recent Stripe transactions"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-zinc-950 text-white">
            <CreditCard className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
              Payments
            </p>
            <h3 className="font-heading text-lg font-bold text-zinc-950">
              Stripe transactions
              {pubSlug ? (
                <span className="ml-2 text-sm font-normal text-zinc-500">
                  · filtered to{" "}
                  <span className="font-mono text-zinc-950">{pubSlug}</span>
                </span>
              ) : null}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {onPubSlugChange ? (
            <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Pub
              <select
                value={pubSlug}
                onChange={(e) => onPubSlugChange(e.target.value)}
                data-testid="stripe-tx-pub-filter"
                className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-xs font-semibold text-zinc-950 tracking-normal focus:outline-none focus:ring-2 focus:ring-[#0038FF]"
              >
                <option value="">All organisations</option>
                {pubOptions.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            onClick={load}
            disabled={state.kind === "loading"}
            data-testid="stripe-tx-refresh"
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-950 disabled:text-zinc-300"
          >
            Refresh
          </button>
        </div>
      </header>

      {/* KPI strip */}
      {state.kind === "ready" ? (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-sm border border-zinc-200 p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Paid (recent)
            </p>
            <p
              className="mt-1 font-heading text-2xl font-bold text-zinc-950 tabular-nums"
              data-testid="stripe-tx-count-paid"
            >
              {paidRows.length}
            </p>
          </div>
          <div className="rounded-sm border border-zinc-200 p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Refunded (recent)
            </p>
            <p
              className="mt-1 font-heading text-2xl font-bold text-zinc-950 tabular-nums"
              data-testid="stripe-tx-count-refunded"
            >
              {refundedRows.length}
            </p>
          </div>
        </div>
      ) : null}

      {state.kind === "loading" ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 text-zinc-400 animate-spin" />
        </div>
      ) : state.kind === "error" ? (
        <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-none" />
          {state.message}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-sm border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
          No paid Stripe transactions yet.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
          {rows.map((r) => {
            const canRefund = r.payment_status === "paid";
            const isBusy = refunding === r.session_id;
            return (
              <li
                key={r.session_id}
                className="p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`stripe-tx-row-${r.session_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-heading text-sm font-bold text-zinc-950 truncate">
                      {r.user_email || r.user_name || "unknown"}
                    </p>
                    <StatusPill paymentStatus={r.payment_status} />
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-500 tabular-nums">
                    {formatMatchDate(r.applied_match_date || r.match_date)}
                    <span className="mx-1.5">·</span>
                    #{(r.session_id || "").slice(-8).toUpperCase()}
                    {r.created_at ? (
                      <>
                        <span className="mx-1.5">·</span>
                        {formatWhen(r.created_at)}
                      </>
                    ) : null}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-heading text-base font-bold text-zinc-950 tabular-nums">
                    {formatMoney(r.amount_pence, r.currency)}
                  </p>
                  {canRefund ? (
                    <button
                      type="button"
                      onClick={() => handleRefund(r)}
                      disabled={isBusy}
                      data-testid={`stripe-tx-refund-${r.session_id}`}
                      className="h-9 px-3 rounded-sm border border-red-300 bg-white text-red-700 hover:bg-red-50 font-heading font-bold text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {isBusy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <Undo2 className="h-3.5 w-3.5" />
                          Refund
                        </>
                      )}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
